import ScanCamera from './ScanCamera.jsx';
import SignaturePad from './SignaturePad.jsx';
import AppointmentBooking from './AppointmentBooking.jsx';
import PhotoCapture from './PhotoCapture.jsx';
import {
  PinIcon, QrCodeIcon, SignatureIcon, CameraIcon, CalendarIcon, CheckIcon,
} from '../common/icons.jsx';

/**
 * Renders the content for the current delivery step.
 * Each step features a large icon, clear instructions, and full-width action buttons.
 *
 * @param {object} props - Step state and callbacks
 */
const DEPOT = { lat: 42.54957, lng: 9.471405 };

export default function StepContent({
  step, currentStop, scanned, online, arrivalTime, showSignature,
  onArrive, onScanResult, onFinishScan, onShowSignature,
  onSignature, onCancelSignature, onPhotoCaptured, onPhotoSkip,
  onAppointment, onSkipAppointment, onComplete, onProblem, plan, isLastStop,
  isDelivered, existingData,
}) {
  const unitsExpected = currentStop?.units_count || 0;
  const scannedSerials = scanned.map((s) => s.serialNumber);

  if (step === 'arrive') {
    // Review mode: show arrival info
    if (isDelivered && existingData?.stop) {
      const s = existingData.stop;
      return (
        <StepCard icon={<PinIcon size={40} className="text-[#059669]" />}
          title="Arrivée enregistrée"
          subtitle={`Arrêt ${(currentStop?.stop_order || 0)}/${plan?.stops?.length || '?'}`}>
          <div className="bg-emerald-50 rounded-xl p-4 space-y-2 text-sm text-left">
            {s.actualArrival && (
              <p className="text-[#0f172a]">Arrivée : <span className="font-semibold">{new Date(s.actualArrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></p>
            )}
            {s.actualDeparture && (
              <p className="text-[#0f172a]">Départ : <span className="font-semibold">{new Date(s.actualDeparture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></p>
            )}
            {s.driverName && <p className="text-[#64748b]">Livreur : {s.driverName}</p>}
          </div>
        </StepCard>
      );
    }

    const navUrl = currentStop?.lat && currentStop?.lng
      ? (/iPad|iPhone|iPod/.test(navigator.userAgent)
          ? `maps://maps.apple.com/?daddr=${currentStop.lat},${currentStop.lng}&dirflg=d`
          : `https://www.google.com/maps/dir/?api=1&destination=${currentStop.lat},${currentStop.lng}&travelmode=driving`)
      : null;

    const stopsRemaining = plan?.stops
      ? plan.stops.filter((s) => s.status === 'pending' || s.status === 'in_progress').length
      : 0;
    const totalUnitsToday = plan?.stops?.reduce((sum, s) => sum + (s.units_count || 0), 0) || 0;
    const isFirstStop = currentStop?.stop_order === 1 && !arrivalTime;

    return (
      <StepCard icon={<PinIcon size={40} className="text-[#059669]" />}
        title="Êtes-vous arrivé chez le client ?"
        subtitle={`${unitsExpected} unité(s) à livrer — Arrêt ${(currentStop?.stop_order || 0)}/${plan?.stops?.length || '?'}`}>
        {isFirstStop && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg py-2.5 px-3 -mt-2 mb-1">
            <p className="text-amber-800 text-sm font-semibold text-center">Chargez {totalUnitsToday} unités avant de partir</p>
          </div>
        )}
        {stopsRemaining > 1 && (
          <p className="text-xs text-[#64748b] bg-gray-50 rounded-lg py-2 px-3 -mt-2 mb-1">
            Encore {stopsRemaining - 1} arrêt(s) après celui-ci
          </p>
        )}
        {navUrl && (
          <a href={navUrl} target="_blank" rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#1e40af] text-white rounded-xl py-3 text-sm font-semibold">
            🧭 Naviguer vers le client
          </a>
        )}
        {currentStop?.phone && (
          <a href={`tel:${currentStop.phone}`}
            className="w-full flex items-center justify-center gap-2 border-2 border-[#1e40af] text-[#1e40af] rounded-xl py-3 text-sm font-semibold">
            📞 Appeler le client
          </a>
        )}
        <PrimaryButton color="green" onClick={() => { if (navigator.vibrate) navigator.vibrate(100); onArrive(); }}>
          Je suis arrivé
        </PrimaryButton>
        <button onClick={onProblem}
          className="w-full flex items-center justify-center gap-2 border-2 border-[#dc2626] text-[#dc2626] rounded-xl py-3 text-sm font-semibold">
          ⚠️ Client absent / Problème
        </button>
      </StepCard>
    );
  }

  if (step === 'scan') {
    const existingItems = existingData?.items || [];
    return (
      <div className="space-y-4">
        {/* Show existing scans if delivered */}
        {isDelivered && existingItems.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-[#0f172a] mb-2">Scans enregistrés ({existingItems.length})</h4>
            <ul className="space-y-1.5">
              {existingItems.map((item) => (
                <li key={item.serial_number} className="text-sm flex justify-between items-center bg-emerald-50 rounded-lg px-3 py-2">
                  <span className="font-mono text-xs text-[#0f172a]">{item.serial_number}</span>
                  <div className="flex items-center gap-2">
                    {item.scan_confidence != null && (
                      <span className={`text-[10px] font-medium ${item.scan_confidence >= 0.8 ? 'text-[#059669]' : 'text-[#f97316]'}`}>
                        {Math.round(item.scan_confidence * 100)}%
                      </span>
                    )}
                    <span className="text-[10px] text-[#64748b] bg-white px-2 py-0.5 rounded">
                      {item.scan_method || 'N/A'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <QrCodeIcon size={22} className="text-[#1e40af]" />
              <h3 className="font-bold text-[#0f172a]">{isDelivered ? 'Rescanner une unité' : 'Scan des unités'}</h3>
            </div>
            {!isDelivered && <ScanCounter current={scanned.length} total={unitsExpected} />}
          </div>
          <ScanCamera onScanResult={onScanResult} offline={!online} scannedSerials={scannedSerials} />
        </div>
        {scanned.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-[#0f172a] mb-2">Nouveaux scans :</h4>
            <ul className="space-y-1.5">
              {scanned.map((s, i) => (
                <li key={i} className="text-sm flex justify-between items-center bg-blue-50 rounded-lg px-3 py-2">
                  <span className="font-mono text-xs text-[#0f172a]">{s.serialNumber}</span>
                  <span className="text-[10px] text-[#64748b] bg-white px-2 py-0.5 rounded">{s.method}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!isDelivered && (
          <PrimaryButton color="blue" onClick={onFinishScan}>
            Passer à la signature
          </PrimaryButton>
        )}
      </div>
    );
  }

  if (step === 'signature') {
    if (showSignature) {
      return (
        <SignaturePad
          signerName={currentStop?.contact_name || ''}
          onConfirm={onSignature}
          onCancel={onCancelSignature}
        />
      );
    }

    const existingSig = existingData?.signature;

    return (
      <StepCard icon={<SignatureIcon size={40} className="text-[#1e40af]" />}
        title={isDelivered && existingSig ? 'Signature enregistrée' : 'Signature du client'}
        subtitle={isDelivered && existingSig ? 'Vous pouvez re-signer si nécessaire' : 'Faites signer le client sur le pad'}>
        {isDelivered && existingSig && (
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 -mt-2 mb-2">
            <img src={existingSig.signature_data} alt="Signature" className="max-h-24 mx-auto" />
            <div className="flex justify-between items-center mt-2 text-[10px] text-[#64748b]">
              {existingSig.signer_name && <span>Signé par : {existingSig.signer_name}</span>}
              {existingSig.signed_at && <span>{new Date(existingSig.signed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
          </div>
        )}
        <PrimaryButton color="blue" onClick={onShowSignature}>
          {isDelivered && existingSig ? 'Nouvelle signature' : 'Ouvrir le pad de signature'}
        </PrimaryButton>
      </StepCard>
    );
  }

  if (step === 'photo') {
    const existingPhoto = existingData?.stop?.photoUrl;
    return (
      <div className="space-y-4">
        {isDelivered && existingPhoto && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-[#0f172a] mb-2 flex items-center gap-2">
              <CameraIcon size={16} /> Photo enregistrée
            </h4>
            <img src={existingPhoto} alt="Photo de livraison" className="w-full max-h-48 object-cover rounded-xl border border-gray-200" />
            <p className="text-xs text-[#64748b] mt-2 text-center">Vous pouvez reprendre une photo ci-dessous</p>
          </div>
        )}
        <PhotoCapture onPhotoCaptured={onPhotoCaptured} onSkip={onPhotoSkip} />
      </div>
    );
  }

  if (step === 'appointment') {
    const existingAppt = existingData?.appointment;
    const APPT_STATUS = { scheduled: 'Planifié', completed: 'Terminé', no_show: 'Absent', cancelled: 'Annulé' };
    return (
      <div className="space-y-4">
        {isDelivered && existingAppt && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-[#0f172a] mb-2 flex items-center gap-2">
              <CalendarIcon size={16} /> RDV enregistré
            </h4>
            <div className="bg-blue-50 rounded-xl p-3 text-sm space-y-1">
              <p className="text-[#0f172a]">Date : <span className="font-semibold">{new Date(existingAppt.scheduled_date).toLocaleDateString('fr-FR')}</span></p>
              <p className="text-[#64748b]">Statut : {APPT_STATUS[existingAppt.status] || existingAppt.status}</p>
              {existingAppt.notes && <p className="text-[#64748b]">Notes : {existingAppt.notes}</p>}
            </div>
            <p className="text-xs text-[#64748b] mt-2 text-center">Vous pouvez reprogrammer ci-dessous</p>
          </div>
        )}
        <AppointmentBooking
          calendlyUrl={plan?.calendly_url}
          onBooked={onAppointment}
          onSkip={onSkipAppointment}
        />
      </div>
    );
  }

  if (step === 'done') {
    const alreadyDelivered = currentStop?.status === 'delivered';

    const depotMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${DEPOT.lat},${DEPOT.lng}`;
    const depotWazeUrl = `https://waze.com/ul?ll=${DEPOT.lat},${DEPOT.lng}&navigate=yes`;

    return (
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-8 text-center space-y-4 shadow-sm">
        <div className="w-16 h-16 bg-[#059669] rounded-full flex items-center justify-center mx-auto">
          <CheckIcon size={32} className="text-white" />
        </div>
        <p className="text-xl font-bold text-[#059669]">
          {alreadyDelivered ? 'Livraison déjà effectuée' : 'Livraison terminée !'}
        </p>
        {scanned.length > 0 && (
          <p className="text-sm text-[#64748b]">{scanned.length} unité(s) scannée(s)</p>
        )}
        {arrivalTime && (
          <p className="text-xs text-[#64748b]">
            Arrivé à {new Date(arrivalTime).toLocaleTimeString('fr-FR')}
          </p>
        )}
        {alreadyDelivered && (
          <p className="text-xs text-[#64748b] bg-white rounded-xl px-4 py-3 border border-gray-200">
            Cliquez sur les étapes ci-dessus (Scan, Signature, Photo, RDV) pour revoir ou modifier les informations enregistrées.
          </p>
        )}
        {isLastStop ? (
          <div className="space-y-3 pt-2">
            <p className="text-sm font-semibold text-[#0f172a]">Tournée terminée ! Retour au dépôt :</p>
            <div className="flex gap-3">
              <a href={depotMapsUrl} target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#059669] hover:bg-[#047857] text-white rounded-xl py-3 font-medium text-sm">
                Maps → Dépôt
              </a>
              <a href={depotWazeUrl} target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#0891b2] hover:bg-[#0e7490] text-white rounded-xl py-3 font-medium text-sm">
                Waze → Dépôt
              </a>
            </div>
          </div>
        ) : (
          <PrimaryButton color="green" onClick={onComplete}>
            Arrêt suivant
          </PrimaryButton>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Card wrapper for step content with icon and text.
 * @param {{ icon: JSX.Element, title: string, subtitle?: string, children: React.ReactNode }} props
 */
function StepCard({ icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 text-center space-y-5 shadow-sm">
      <div className="flex justify-center">{icon}</div>
      <div>
        <p className="text-lg font-bold text-[#0f172a]">{title}</p>
        {subtitle && <p className="text-sm text-[#64748b] mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * Animated scan counter with color transition.
 * @param {{ current: number, total: number }} props
 */
function ScanCounter({ current, total }) {
  const complete = current >= total && total > 0;
  return (
    <span className={`text-lg font-bold px-3 py-1 rounded-xl transition-all ${
      complete
        ? 'text-[#059669] bg-emerald-50 scale-105'
        : 'text-[#1e40af] bg-blue-50'
    }`}>
      {current}/{total}
    </span>
  );
}

/**
 * Full-width primary action button.
 * @param {{ color: 'green'|'blue', onClick: () => void, children: React.ReactNode }} props
 */
function PrimaryButton({ color, onClick, children }) {
  const colors = {
    green: 'bg-[#059669] hover:bg-[#047857] active:bg-[#065f46]',
    blue: 'bg-[#1e40af] hover:bg-[#1e3a8a] active:bg-[#172554]',
  };
  return (
    <button onClick={onClick}
      className={`w-full text-white rounded-xl py-4 text-base font-bold min-h-[56px] transition-all active:scale-[0.98] ${colors[color]}`}>
      {children}
    </button>
  );
}
